import type { IBaseCampaign } from '~/modules/campaign/types';
import type { IAssetTemplateAssigned } from '../types';

import { getExistingCampaigns, getUsedPageIds } from '~/modules/campaign-group-template/helpers';
import { IndexTableSelectionType, useIndexResourceState } from '@shopify/polaris';
import { IAssetTemplateAssignedModalType } from '~/modules/asset-template/types';
import { useAssetTemplateContext } from '~/modules/asset-template';
import { useCallback, useEffect, useMemo, useState } from 'react';

export interface IAssetTemplateAssignedPickerProps {
  campaigns: IBaseCampaign[];
  pageAssignedData: IAssetTemplateAssigned[];
  allPageAssignedDataIds: string[];
}
export interface IAssetTemplateAssignedPickerResult {
  disabledAssignedId?: string;
  allResourcesSelected: boolean;
  pageAssignedFilteredDisabled: IAssetTemplateAssigned[];
  selectedResources: string[];
  allPageAssignedSorted: IAssetTemplateAssigned[];
  handleSelectionChange: (
    type: IndexTableSelectionType,
    isSelecting: boolean,
    item: string,
  ) => void;
  handleSetRefreshAssigned: () => void;
  handleSetSelectedItem: (item: string) => void;
}

export const useInitializeAssetTemplateAssignedPicker = (
  props: IAssetTemplateAssignedPickerProps,
): IAssetTemplateAssignedPickerResult => {
  const [isRefreshAssigned, setIsRefreshAssigned] = useState(false);
  const [disabledAssignedId, setDisabledAssignedId] = useState<string>();

  const { campaigns, pageAssignedData, allPageAssignedDataIds } = props;
  const { modalAssetTemplateAssigned, isLoadingAsset } = useAssetTemplateContext();
  const { modalAssignedType, modalAssignedData } = modalAssetTemplateAssigned;

  const campaign = modalAssignedData?.campaign;
  const variant = modalAssignedData?.variant;
  const conditions = campaign?.conditions;
  const templateSuffix = variant?.templateSuffix ?? '';

  const targetPageIds = useMemo(() => (campaign ? getUsedPageIds([campaign]) : []), [campaign]);
  const campaignGroupCampaignIgnoreCurrentCampaigns = useMemo(
    () => campaigns?.filter((c) => c.id !== campaign?.id),
    [campaign?.id, campaigns],
  );
  const existingCampaigns = useMemo(
    () => getExistingCampaigns(templateSuffix, campaignGroupCampaignIgnoreCurrentCampaigns || []),
    [campaignGroupCampaignIgnoreCurrentCampaigns, templateSuffix],
  );
  const usedPageIds = useMemo(() => getUsedPageIds(existingCampaigns), [existingCampaigns]);
  const selectedResourceInitial = useMemo(() => {
    if (modalAssignedType === IAssetTemplateAssignedModalType.VARIANT_B) {
      return [];
    }

    return targetPageIds.length ? targetPageIds : allPageAssignedDataIds;
  }, [modalAssignedType, targetPageIds, allPageAssignedDataIds]);
  const allPageAssignedSorted = useMemo(() => {
    if (modalAssignedType === IAssetTemplateAssignedModalType.VARIANT_B) {
      const redirectURL = modalAssignedData?.variant?.template?.redirectURL;
      const currentSelectedUrl = pageAssignedData.filter((item) => redirectURL === item.url);
      const availableUrls = pageAssignedData.filter((item) => !currentSelectedUrl.includes(item));

      return [...currentSelectedUrl, ...availableUrls];
    }

    const currentSelectedUrl = pageAssignedData.filter((item) =>
      conditions?.targetPageIds?.includes(item?.id),
    );
    const availableUrls = pageAssignedData.filter(
      (item) => !conditions?.targetPageIds?.includes(item?.id) && !usedPageIds.includes(item?.id),
    );
    const usedOtherUrls = pageAssignedData.filter((item) => usedPageIds.includes(item?.id));
    const sortedUrls = currentSelectedUrl
      ? [...currentSelectedUrl, ...availableUrls, ...usedOtherUrls]
      : [...availableUrls, ...usedOtherUrls];

    return sortedUrls.map((item) => ({
      ...item,
      disabled: !!usedPageIds.includes(item?.id),
    }));
  }, [modalAssignedType, pageAssignedData, modalAssignedData, conditions, usedPageIds]);
  const pageAssignedFilteredDisabled = useMemo(() => {
    return allPageAssignedSorted.filter(
      (item) => !item?.disabled && item?.id !== disabledAssignedId,
    );
  }, [allPageAssignedSorted, disabledAssignedId]);

  const { allResourcesSelected, selectedResources, handleSelectionChange, clearSelection } =
    useIndexResourceState<IAssetTemplateAssigned>(allPageAssignedSorted, {
      selectedResources: selectedResourceInitial,
      resourceFilter: ({ disabled, id }) => !disabled && id !== selectedResourceInitial[0],
    });

  const handleSetSelectedItem = useCallback(
    (newItem: string) => {
      const prevSet = new Set(selectedResources || []);
      const isSelecting = !prevSet.has(newItem);
      handleSelectionChange(IndexTableSelectionType.Single, isSelecting, newItem);
    },
    [handleSelectionChange, selectedResources],
  );

  const handleSetRefreshAssigned = useCallback(() => {
    if (targetPageIds.length) return;

    clearSelection();
    setIsRefreshAssigned(true);
  }, [clearSelection, targetPageIds]);

  const handleSetSelectionAfterRefresh = useCallback(() => {
    if (isLoadingAsset) return;
    if (!isRefreshAssigned) return;
    if (targetPageIds.length) return;

    allPageAssignedDataIds.forEach((id) => {
      const prevSet = new Set(selectedResources || []);
      const isSelected = prevSet.has(id);
      if (!isSelected) {
        handleSelectionChange(IndexTableSelectionType.Single, true, id);
      }
    });
    setIsRefreshAssigned(false);
  }, [
    isLoadingAsset,
    isRefreshAssigned,
    selectedResources,
    targetPageIds.length,
    allPageAssignedDataIds,
    handleSelectionChange,
  ]);

  useEffect(() => {
    handleSetSelectionAfterRefresh();
  }, [handleSetSelectionAfterRefresh]);

  useEffect(() => {
    if (selectedResources.length === 0 && pageAssignedFilteredDisabled.length) {
      setDisabledAssignedId(pageAssignedFilteredDisabled[0].id);
      !!disabledAssignedId && handleSetSelectedItem(disabledAssignedId);
    }

    if (!disabledAssignedId) return;
    setDisabledAssignedId(undefined);
  }, [handleSetSelectedItem, pageAssignedFilteredDisabled, selectedResources, disabledAssignedId]);

  return {
    disabledAssignedId,
    allResourcesSelected,
    pageAssignedFilteredDisabled,
    selectedResources,
    allPageAssignedSorted,
    handleSelectionChange,
    handleSetRefreshAssigned,
    handleSetSelectedItem,
  };
};
